/**
 * The document sync audit's pure decisions — sorting, filtering, selection, and what a row's status
 * is during a run — kept out of the dialog so each is directly testable.
 *
 * The run-status half exists because the backend deliberately has **no progress store**: detection is
 * a live query, so filing is observed by re-reading the audit and watching rows drop off as they gain
 * receipts. Turning "the row is gone" into "Filed" is a frontend inference, and it is the one piece
 * of this feature that has to be reasoned about rather than read off a payload.
 */

import type {
  DocumentSyncAuditItem,
  DocumentSyncAuditItemKind,
  DocumentSyncAuditSkippedItem
} from './types';

/**
 * The most items one run will accept.
 *
 * **Mirrors `MAX_DOCUMENT_SYNC_AUDIT_ITEMS_PER_RUN` in the backend's
 * `dto/document-sync-audit-sync.dto.ts`, which is the authority** — it is a correctness bound there,
 * not a politeness one: the run holds a single-flight lock whose finite TTL is the only thing stopping
 * a crashed run wedging the firm out, so the work it covers has to be finite too. Exceeding it is a
 * `400`, so the cap is repeated here purely to stop the UI offering a request that cannot succeed.
 * A larger backlog files in batches, which costs nothing because the list self-heals.
 */
export const MAX_DOCUMENT_SYNC_AUDIT_ITEMS_PER_RUN = 100;

/** What the table can be ordered by. */
export type DocumentSyncAuditSortField = 'type' | 'matter' | 'date';

export type SortDirection = 'asc' | 'desc';

export interface DocumentSyncAuditSort {
  field: DocumentSyncAuditSortField;
  direction: SortDirection;
}

/**
 * The default order — newest first, matching the order the backend already returns.
 *
 * So the first render never reorders what arrived, and a user who never touches a header sees the
 * report exactly as the endpoint described it.
 */
export const DEFAULT_DOCUMENT_SYNC_AUDIT_SORT: DocumentSyncAuditSort = {
  field: 'date',
  direction: 'desc'
};

/**
 * A row's identity across polls.
 *
 * **`kind` is part of the key, not decoration.** A correspondence id and a file id are both plain
 * integers drawn from different tables, so they collide freely — keying on `itemId` alone would let
 * one row's dispatch mark an unrelated row as filed.
 */
export function auditItemKey(
  item: Pick<DocumentSyncAuditItem, 'itemId' | 'kind'>
): string {
  return `${item.kind}:${item.itemId}`;
}

/**
 * The text the Matter column shows for a row, whose matter may have no reference of its own.
 *
 * One definition, because two surfaces need the same string for different reasons: the table renders
 * it and the filter matches against it. An unreferenced matter reads as `Matter <id>` on screen, so a
 * filter that rebuilt that fallback itself would be a second definition of the label — and the first
 * time either moved, typing what the screen plainly says would quietly stop matching. Unreferenced is
 * not an edge case to wave at either: it is every matter the firm has not given a reference to.
 */
export function auditMatterLabel(
  item: Pick<DocumentSyncAuditItem, 'matterReference' | 'matterId'>
): string {
  return item.matterReference || `Matter ${item.matterId}`;
}

/** Sort order for the type column — correspondence first, so the two kinds group predictably. */
const KIND_ORDER: Record<DocumentSyncAuditItemKind, number> = {
  correspondence: 0,
  file: 1
};

/**
 * Order the rows, stably.
 *
 * Every comparison falls through to the date and then the key, so two rows that tie on the chosen
 * column keep one fixed order across re-renders and polls — a table that reshuffles its ties while a
 * run is in flight reads as rows moving on their own.
 */
export function sortAuditItems(
  items: DocumentSyncAuditItem[],
  sort: DocumentSyncAuditSort
): DocumentSyncAuditItem[] {
  const sign = sort.direction === 'asc' ? 1 : -1;

  return [...items].sort((a, b) => {
    const primary = comparePrimary(a, b, sort.field);
    if (primary !== 0) return primary * sign;

    // Tie-breakers are deliberately NOT sign-flipped: they exist to make the order deterministic,
    // not to be part of what the user asked to sort by.
    const byDate = Date.parse(b.date) - Date.parse(a.date);
    if (byDate !== 0) return byDate;

    return auditItemKey(a).localeCompare(auditItemKey(b));
  });
}

function comparePrimary(
  a: DocumentSyncAuditItem,
  b: DocumentSyncAuditItem,
  field: DocumentSyncAuditSortField
): number {
  switch (field) {
    case 'type':
      return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
    case 'matter':
      return compareMatter(a, b);
    case 'date':
      return Date.parse(a.date) - Date.parse(b.date);
  }
}

/**
 * Matter order, by the firm's own reference where it has one.
 *
 * A matter with no reference sorts **after** every matter that has one rather than under an empty
 * string, so the referenced matters stay contiguous instead of being split by blanks; unreferenced
 * ones then order by matter id, which is at least stable and monotonic in age.
 */
function compareMatter(
  a: DocumentSyncAuditItem,
  b: DocumentSyncAuditItem
): number {
  if (a.matterReference && b.matterReference) {
    return a.matterReference.localeCompare(b.matterReference, 'en', {
      numeric: true,
      sensitivity: 'base'
    });
  }
  if (a.matterReference) return -1;
  if (b.matterReference) return 1;
  return a.matterId - b.matterId;
}

/** How many PMS files the chosen rows will produce, so "sync" is never a surprise. */
export function countPmsFiles(items: DocumentSyncAuditItem[]): number {
  return items.reduce((total, item) => total + item.pmsFileCount, 0);
}

// ============================================================================
// Live run status
// ============================================================================

/**
 * What has happened to a row since the run started.
 *
 * - `filing` — dispatched, still owed on the latest read.
 * - `filed` — dispatched and **gone** from the latest read, which is the only evidence of success
 *   the backend produces: a receipt is what removes a row.
 * - `failed` — dispatched, still listed, and now reporting a failure of its own.
 * - `not-owed` — the trigger declined it because the audit no longer listed it. The expected outcome
 *   of a dialog left open while something else filed the item, not an error.
 */
export type AuditRowRunStatus = 'filing' | 'filed' | 'failed' | 'not-owed';

export interface AuditRunProgress {
  /** Keys dispatched in this run, in the order the table showed them. */
  dispatchedKeys: string[];
  /** The rows as they looked when the run started — the only record of a row that has since gone. */
  snapshot: DocumentSyncAuditItem[];
  /** Keys the trigger reported back as not dispatched. */
  skipped: DocumentSyncAuditSkippedItem[];
}

/** One table row: the item, plus its run status when a run has touched it. */
export interface AuditRow {
  item: DocumentSyncAuditItem;
  runStatus: AuditRowRunStatus | null;
}

/**
 * The rows to render, given the latest audit read and the run in flight.
 *
 * **A filed row is kept on screen rather than allowed to vanish.** The dialog stays open through a
 * run precisely so a partial failure is visible and re-triable, and a row that silently disappeared
 * would be indistinguishable from one that was never selected — leaving the user to compare counts to
 * work out what happened. So a dispatched row absent from the fresh read is re-attached from the
 * snapshot and marked `filed`; it is the disappearance itself that proves it landed.
 *
 * With no run in flight this is exactly the fresh list, so the ordinary case pays nothing for any of
 * the above.
 */
export function buildAuditRows(
  latest: DocumentSyncAuditItem[],
  progress: AuditRunProgress | null,
  sort: DocumentSyncAuditSort
): AuditRow[] {
  if (!progress) {
    return sortAuditItems(latest, sort).map((item) => ({
      item,
      runStatus: null
    }));
  }

  const dispatched = new Set(progress.dispatchedKeys);
  const skippedByKey = new Map(
    progress.skipped.map((entry) => [auditItemKey(entry), entry])
  );
  const latestKeys = new Set(latest.map(auditItemKey));

  // A dispatched row the latest read no longer lists has gained a receipt — the only success signal
  // there is. Re-attach it from the snapshot so it stays visible with its outcome.
  const filed = progress.snapshot.filter(
    (item) =>
      dispatched.has(auditItemKey(item)) && !latestKeys.has(auditItemKey(item))
  );

  return sortAuditItems([...latest, ...filed], sort).map((item) => {
    const key = auditItemKey(item);

    if (!dispatched.has(key)) return { item, runStatus: null };
    if (skippedByKey.has(key)) return { item, runStatus: 'not-owed' };
    if (!latestKeys.has(key)) return { item, runStatus: 'filed' };
    if (item.state === 'failed') return { item, runStatus: 'failed' };

    return { item, runStatus: 'filing' };
  });
}

/**
 * Whether the run still has work outstanding — the poll's stop condition.
 *
 * A run whose every dispatched row has settled needs no further polling: nothing else will move
 * without another user action.
 */
export function isRunSettled(rows: AuditRow[]): boolean {
  return !rows.some((row) => row.runStatus === 'filing');
}

/**
 * Fold a new run's record into whatever the dialog already held.
 *
 * A retry is a second run over the same open dialog, and replacing the record rather than merging it
 * would drop the earlier run's filed rows out of the snapshot — so the outcomes the user is still
 * reading would disappear the moment they retried something else.
 *
 * De-duplicated by key on both halves, so re-dispatching a row that failed the first time leaves one
 * entry rather than two. The incoming skip list wins for a row that appears in both: it describes the
 * more recent attempt.
 */
export function mergeRunProgress(
  previous: AuditRunProgress | null,
  incoming: AuditRunProgress
): AuditRunProgress {
  if (!previous) return incoming;

  const snapshotByKey = new Map(
    [...previous.snapshot, ...incoming.snapshot].map((item) => [
      auditItemKey(item),
      item
    ])
  );
  const skippedByKey = new Map(
    [...previous.skipped, ...incoming.skipped].map((entry) => [
      auditItemKey(entry),
      entry
    ])
  );

  return {
    // `Array.from` rather than spreading the iterators — this package's tsconfig target rejects
    // `[...aSet]` (TS2802), and the whole file has to compile for any of it to run.
    dispatchedKeys: Array.from(
      new Set([...previous.dispatchedKeys, ...incoming.dispatchedKeys])
    ),
    snapshot: Array.from(snapshotByKey.values()),
    skipped: Array.from(skippedByKey.values())
  };
}

/**
 * What the dialog is watching, expressed without reference to the audit read.
 *
 * Two shapes because the two runs prove completion differently: a run this dialog dispatched knows
 * its own row keys, while a run it merely **attached** to via the `409` knows nothing about which
 * rows belong to it — so the only evidence it has finished is the list draining.
 *
 * It carries no query data on purpose. The poll's stop condition has to be decidable *before* the
 * read it gates, or the two become mutually dependent: the interval would need the rows, the rows
 * would need the fetch, and the fetch would need the interval.
 */
export type AuditRunWatch =
  | { kind: 'dispatched'; dispatchedKeys: string[] }
  | { kind: 'attached' };

/**
 * Whether another read of the audit could still show something new.
 *
 * `undefined` items means nothing has been read yet, which is outstanding by definition rather than
 * settled — reading absence as "done" would stop the poll before it ever started.
 *
 * A **failed** row is settled: it will not move again on its own, and the user has to act. This is
 * what stops a permanently-failing row polling forever.
 */
export function runHasOutstandingWork(
  watch: AuditRunWatch | null,
  items: DocumentSyncAuditItem[] | undefined
): boolean {
  if (!watch) return false;
  if (items === undefined) return true;

  if (watch.kind === 'attached') return items.length > 0;

  const byKey = new Map(items.map((item) => [auditItemKey(item), item]));

  return watch.dispatchedKeys.some((key) => {
    const stillOwed = byKey.get(key);
    // Gone → filed, so it has settled. Present and failed → settled too.
    return !!stillOwed && stillOwed.state !== 'failed';
  });
}

// ============================================================================
// Filtering
// ============================================================================

/**
 * Whether a row matches what the user typed.
 *
 * Matched against the two things a solicitor already knows when they open this list: which estate the
 * document belongs to, and what the document is called (`detail` — a correspondence's subject, or a
 * standalone file's name). One query across both fields rather than a control per field, because
 * "work this matter's backlog" and "find that one document" are the same gesture with different text
 * in it.
 *
 * Case-insensitive substring, so a partial reference and a partial filename both narrow the list. The
 * matter side goes through `auditMatterLabel` — that is what keeps a row whose matter has no
 * reference findable by the `Matter <id>` the table is actually showing for it.
 */
export function matchesAuditFilter(
  item: DocumentSyncAuditItem,
  query: string
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return (
    auditMatterLabel(item).toLowerCase().includes(needle) ||
    item.detail.toLowerCase().includes(needle)
  );
}

/**
 * Narrow the rows to those matching the filter.
 *
 * A step **after** `buildAuditRows` on purpose, not a parameter to it. That function decides what a
 * row's run outcome is — including re-attaching a filed row that has already left the audit — and a
 * view filter has no business inside that inference. Separating them is also what lets a hidden row
 * go on filing normally: the poll's stop condition reads the audit items rather than these rows, so
 * narrowing the view can never strand a run still working on something out of sight.
 *
 * Order is preserved, so the active sort still orders whatever remains. An empty query returns the
 * very same array, so the untouched case costs nothing and does not churn referential equality.
 */
export function filterAuditRows(rows: AuditRow[], query: string): AuditRow[] {
  if (!query.trim()) return rows;
  return rows.filter((row) => matchesAuditFilter(row.item, query));
}
